<template>
    <div
        class="card"
        :class="{ 'has-bottom': hasBottom }"
    >
        <div class="header">
            <CardTitle
                :item="program"
            />
            <Close @click="close"/>
        </div>
        <div class="content"
            v-loading="!program.moment || isRemoving"
        >
            <h2 class="program-name">{{ title }}</h2>
            <div v-if="program.institute" class="institution">
                <img
                    v-if="program.institute.logo"
                    class="logo"
                    :src="program.institute.logo" alt=""
                >
                <div
                    v-else
                    class="logo-stub"
                    :class="{ rsv: isCourse || isWebinar }"
                />
                <div class="name">{{ program.institute.name }}</div>
            </div>
            <ul>
                <li>Дата начала: <span
                        v-if="startAt"
                        class="bold"
                    >{{ startAt }}</span></li>
                <li v-if="ItemDuration">Длительность: <span
                    v-if="startAt"
                    class="bold"
                >{{ ItemDuration }}</span></li>
            </ul>
            <CardLinks
                :item="program"
                course-link-text="Перейти к курсу"
            />
            <button
                v-if="program.status !== 'completed'"
                class="btn-delete"
                @click="onAction({ key: 'cancel', close: true })"
                :disabled="isLoading"
            >
                Удалить
            </button>
        </div>
        <div class="bottom" v-if="hasBottom">
            <template v-if="view !== null">
                <CompletedView
                    v-if="view === 'completed'"
                    @action="onAction"
                    :item="program"
                    v-loading="isLoading"
                />
                <CanceledView
                    v-if="view === 'canceled'"
                    @action="onAction"
                    :item="program"
                    v-loading="isLoading"
                />
                <StartedView
                    v-if="view === 'started'"
                    @action="onAction"
                    :item="program"
                    v-loading="isLoading"
                />
            </template>
            <template v-else>
                <div class="completed" v-if="isCompleted">
                    <img src="../../../../assets/images/account-track/medal.svg" alt="">
                    Обучение пройдено
                </div>
                <template v-if="program.cardQuestions.isLmsUpdate">
                    <RemoveQuestion
                        v-if="program.educationType === 'rsv_webinar'"
                        @action="onAction"
                        :item="program"
                        v-loading="isLoading"
                    />
                    <ScheduleQuestion
                        v-else
                        @action="onAction"
                        :item="program"
                        :loading="isLoading"
                    />
                </template>
                <StartedQuestion
                    v-else-if="isStartedQuestionShown"
                    @action="onAction"
                    :item="program"
                    v-loading="isLoading"
                />
                <CompleteQuestion
                    v-else-if="isCompletedQuestionShown"
                    @action="onAction"
                    :item="program"
                    v-loading="isLoading"
                />
            </template>
        </div>
    </div>
</template>

<script>
import Close from '../../../UI/Close';
import CardTitle from './Title';
import CardLinks from './Links';
import CompletedView from './Views/CompletedView';
import StartedView from './Views/StartedView';
import CanceledView from './Views/CanceledView';
import StartedQuestion from './Questions/StartedQuestion';
import CompleteQuestion from './Questions/CompleteQuestion';
import ScheduleQuestion from './Questions/ScheduleQuestion';
import RemoveQuestion from './Questions/RemoveQuestion';

import { parseDuration } from '../../../../tools/humanize_duration';
import {mapActions} from 'vuex';

const actions = [
    'startProgram',
    'completeProgram',
    'cancelProgram',
    'removeProgram',
    'scheduleProgram',
];

export default {
    name: 'Program',
    props: {
        program: {
            type: Object,
            required: true,
        },
        module: {
            type: Object,
            required: false,
        },
    },
    data: () => ({
        view: null,
        isLoading: false,
        isRemoving: false,
    }),
    components: {
        CardTitle,
        CardLinks,
        Close,
        CompletedView,
        StartedView,
        CanceledView,
        StartedQuestion,
        CompleteQuestion,
        ScheduleQuestion,
        RemoveQuestion,
    },
    watch: {
        program(val) {
            this.view = null;
        },
        module() {
            this.view = null;
        },
    },
    methods: {
        ...mapActions('accountTrack', actions),
        close() {
            this.$emit('close');
        },
        async onAction(v) {
            this.isLoading = true;
            switch (v.key) {
                case 'start':
                    await this.startProgram({ programId: this.program.id, moduleId: this.moduleId });
                    this.view = 'started';
                    break;
                case 'complete':
                    await this.completeProgram({ programId: this.program.id, moduleId: this.moduleId });
                    this.view = 'completed';
                    break;
                case 'cancel':
                    this.isRemoving = true;
                    await this.cancelProgram({ programId: this.program.id });
                    this.isRemoving = false;
                    break;
                case 'remove':
                    await this.cancelProgram({ programId: this.program.id });
                    await this.removeProgram({ programId: this.program.id });
                    break;
                case 'schedule':
                    await this.cancelProgram({ programId: this.program.id });
                    await this.scheduleProgram({ programId: this.program.id, startAt: v.date });
                    break;
                case 'view_cancel':
                    this.view = 'canceled';
                    break;
            }
            if (v.close === true) {
                window.scrollTo(0, 0);
                this.close();
            }

            this.isLoading = false;

            if (v.redirect) {
                await this.$router.push(v.redirect);
            }
        },
    },
    computed: {
        itemType() {
            return this.module === null ? 'program' : 'module';
        },
        hasBottom() {
            return this.program.status === 'completed'
                || this.view !== null
                || (this.program.cardQuestions && (
                    this.program.cardQuestions.isStarted
                    || this.program.cardQuestions.isCompleted
                    || this.program.cardQuestions.isLmsUpdate
                ));
        },
        canReschedule() {
            return this.program.status !== 'completed';
        },
        title() {
            return [
                this.program.name.trim(),
                this.module ? this.module.name.trim() : null,
            ].filter(v => v)
                .join('. ');
        },
        isStartedQuestionShown() {
            return !this[this.itemType].cardQuestions.isCompleted
                && this[this.itemType].cardQuestions.isStarted;
        },
        isCompletedQuestionShown() {
            return this[this.itemType].cardQuestions.isCompleted;
        },
        moduleId() {
            return this.module ? this.module.id : null;
        },
        ItemDuration () {
            if (this[this.itemType].durationInterval) {
                return parseDuration(this[this.itemType].durationInterval);
            } else return null;
        },
        startAt() {
            return this.program.startAt
                ? this[this.itemType].moment.start.format('DD MMMM YYYY')
                : null;
        },
        isCompleted() {
            return this[this.itemType].status === 'completed';
        },
        isCourse() {
            const allowableTypes = ['rsv_course', 'additional'];
            return this.program.entityType === 'course' && allowableTypes.includes(this.program.educationType);
        },
        isWebinar() {
            const allowableTypes = ['rsv_webinar', 'additional'];
            return this.program.entityType === 'webinar' && allowableTypes.includes(this.program.educationType);
        },
    },
};
</script>

<style scoped lang="scss">
    @import '~@/styles/rem';

    .card {
        background: #FFFFFF;
        border: 1px solid #D2D5D9;
        box-sizing: border-box;
        border-radius: 4px;
        min-height: 341px;
        height: 100%;

        @include on-md {
            border-radius: 4px 4px 0 0;
            border-bottom: none;
        }

        &.has-bottom {
            @include on-md {
                border-radius: 4px;
                border-bottom: 1px solid #D2D5D9;
            }
        }
    }

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 70px;
        border-bottom: 1px solid #D2D5D9;
        padding-left: 24px;
        padding-right: 16px;

        @include on-xs {
            height: 48px;
        }
    }
    .content {
        padding: 24px 24px 32px 24px;

        @include on-xs {
            padding: 12px 16px;
        }

        .program-name {
            margin: 0;
            /*font-family: Proxima Nova;*/
            font-style: normal;
            font-weight: 600;
            font-size: 18px;
            line-height: 30px;
            margin-bottom: 24px;
            color: #2A2626;

            @include on-sm {
                margin: 0 0 24px 0;
                font-size: 22px;
                line-height: 30px;
            }

            @include on-xs {
                margin: 0 0 16px 0;
                font-size: 16px;
                line-height: 24px;
            }
        }
        .institution {
            /*font-family: Proxima Nova;*/
            font-style: normal;
            font-weight: 600;
            font-size: 16px;
            line-height: 24px;
            color: #2A2626;
            align-items: center;
            margin-bottom: 24px;
            display: flex;
            min-height: 80px;

            @include on-xs {
                font-size: 15px;
                line-height: 22px;
            }

            .logo, .logo-stub {
                flex: 0 0 auto;
                width: 80px;
                height: 80px;
                margin-right: 16px;
                object-fit: contain;
                background: no-repeat center/contain;

                @include on-xs {
                    width: 56px;
                    height: 56px;
                    margin-right: 12px;
                }
            }

            .logo-stub {
                background-image: url('~@/assets/images/account-track/institute_stub.svg');

                &.rsv {
                    background-image: url('~@/assets/images/account-track/rsv-stub.svg');
                }
            }
            .name{
                flex: 1;
            }
        }

        ul {
            margin: 0;
            padding: 0;

            li {
                list-style: none;
                /*font-family: Proxima Nova;*/
                font-style: normal;
                font-weight: normal;
                font-size: 16px;
                line-height: 24px;
                color: #2A2626;
                margin-bottom: 8px;

                &:last-of-type {
                    margin-bottom: 0;
                }

                @include on-xs {
                    font-size: 14px;
                    line-height: 20px;
                }

                .bold {
                    font-weight: 600;
                }
            }
        }
    }
    .bottom {
        border-top: 1px solid #D2D5D9;
        padding: 32px 32px 56px;

        @include on-md {
            min-height: auto;
        }

        @include on-sm {
            padding: 24px 24px 40px;
            min-height: auto;
        }
    }


    .completed {
        display: flex;
        justify-content: center;
        align-items: center;

        /*font-family: Proxima Nova;*/
        font-style: normal;
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        text-transform: uppercase;
        color: #2A2626;

        @include on-xs {
            font-size: 14px;
            line-height: 20px;
        }

        img {
            width: 36px;
            height: 36px;
            margin-right: 8px;
        }
    }

    .btn-delete {
        cursor: pointer;
        font-family: 'Proxima Nova', sans-serif;
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        color: #EF627D;
        border: 0;
        outline: none;
        background: none;
        margin: 16px 0 0;
        padding: 0;

        &:hover {
            color: #E75671;
        }

        @include on-xs {
            font-size: 14px;
            line-height: 20px;
        }
    }
</style>